import type { MeResponse, ProcessListItemDto } from '@process-hub/shared';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { StatusPill } from '../components/Badges';
import { describeError } from '../errors';

/** Department library: every process the caller may see. Admin sees all departments. Stale hidden unless asked. */
export function Library({ me }: { me: MeResponse }) {
  const [rows, setRows] = useState<ProcessListItemDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const includeStale = params.get('stale') === '1';
  const navigate = useNavigate();

  useEffect(() => {
    setRows(null);
    api
      .listProcesses(includeStale)
      .then(setRows)
      .catch((e: unknown) => setError(describeError(e)));
  }, [includeStale]);

  // D-039: a role without a department group shows nothing; say why rather than "No processes yet".
  if (!me.isAdmin && me.departments.length === 0) {
    return (
      <div className="notice">
        Your account has a Process Hub role but is not in a department security group (SG-WT-DPT), so there is nothing to show. Ask IT to add you to your department's group; it takes effect at your next sign-in.
      </div>
    );
  }
  if (error) return <div className="notice notice--error">{error}</div>;
  if (!rows) return <div className="notice">Loading processes.</div>;

  return (
    <div className="card">
      <div className="card__header">
        <div className="card__title">{me.isAdmin ? 'All departments' : me.departments.map((d) => d.name).join(', ') || 'Your department'}</div>
        <div className="card__meta">
          <label className="version-pick" style={{ marginRight: 12 }}>
            <input type="checkbox" checked={includeStale} onChange={(e) => setParams(e.target.checked ? { stale: '1' } : {})} /> <span>Show stale and retired</span>
          </label>
          {rows.length} {rows.length === 1 ? 'process' : 'processes'}
          {(me.isAdmin || me.roles.includes('ProcessOwner')) && (
            <>
              {' · '}
              <Link to="/interview">New process</Link>
            </>
          )}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="notice" style={{ margin: 18 }}>
          No processes yet.{' '}
          {me.roles.includes('ProcessOwner') || me.isAdmin ? <Link to="/interview">Start an interview</Link> : 'Your department has not published a process.'}
        </div>
      ) : (
        <table className="grid">
          <thead>
            <tr>
              <th>Process</th>
              <th>Department</th>
              <th>Owner</th>
              <th>Maintained by</th>
              <th>Version</th>
              <th>Status</th>
              <th>Re-certify</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="grid__row--link" onClick={() => navigate(`/processes/${p.slug}`)} data-testid={`process-${p.slug}`}>
                <td>
                  <div className="grid__primary">{p.name}</div>
                  <div className="grid__secondary">{p.slug}</div>
                </td>
                <td>{p.departmentName}</td>
                <td>{p.ownerName ?? <span className="muted">Not yet signed in</span>}</td>
                <td>{p.maintainerRole}</td>
                <td className="mono">{p.currentVersion ?? '—'}</td>
                <td>
                  <StatusPill status={p.status} />
                </td>
                <td className="mono">{p.nextRecertDate ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
